import { Box, Grid, TableCell, TableRow } from '@mui/material';
import React, { useEffect, useState } from 'react';
import PageContainer from '../components/containers/PageContainer';
import CustomTable from '../components/ui/CustomTable';
import StatCard from '../components/common/StatCard';
import { useUserContext } from '../App';
import Icon from '../components/common/Icon';
import api from '../config/axios';
import BarChart from '../components/charts/BarChart';
import Section from '../components/ui/Section';
import { DonutChart, DonutChartPayment } from '../components/charts/DonutChart';

const Dashboard = () => {
    const { user } = useUserContext();
    const [OrderStats, setOrderStats] = useState({});
    const [revenueStats, setRevenueStats] = useState({});
    const [usersStats, setUsersStats] = useState({});
    const [depositStats, setDepositStats] = useState({});
    const [labelTypesData, setLabelTypesData] = useState([]);

    const [ordersBarChartData, setOrdersBarChartData] = useState([]);
    const [usersBarChartData, setUsersBarChartData] = useState([]);
    const [revenueBarChartData, setRevenueBarChartData] = useState({});
    const [depositBarChartData, setDepositBarChartData] = useState([]);
    const [labelPieChartData, setLabelPieChartData] = useState([]);
    const [PaymentPieChartData, setPaymentPieChartData] = useState([]);
    const [netProfitData, setNetProfitData] = useState({});

    // dashboard stats
    const getOrdersStat = async () => {
        await api
            .get('/dashboard/orders-stats')
            .then((res) => {
                setOrderStats(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };
    const getRevenueStats = async () => {
        await api
            .get('/dashboard/revenue-stats')
            .then((res) => {
                setRevenueStats(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getUsersStats = async () => {
        await api
            .get('/dashboard/users-stats')
            .then((res) => {
                setUsersStats(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };
    const getDepositStats = async () => {
        await api
            .get('/dashboard/deposit-stats')
            .then((res) => {
                setDepositStats(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // labeltype stats

    const readLabelTypeStats = async () => {
        await api
            .get('/dashboard/type-stats')
            .then((res) => {
                setLabelTypesData(res.data.labelTypesData);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // barchart data
    const getOrdersBarChart = async () => {
        await api
            .get('/dashboard/read-orders-chart')
            .then((res) => {
                setOrdersBarChartData(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getUsersBarChart = async () => {
        await api
            .get('/dashboard/read-users-chart')
            .then((res) => {
                setUsersBarChartData(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getDepositsBarChart = async () => {
        await api
            .get('/dashboard/read-invoices-chart')
            .then((res) => {
                setDepositBarChartData(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getRevenueBarChart = async () => {
        await api
            .get('/dashboard/read-revenue-chart')
            .then((res) => {
                const formattedData = {
                    labels: res.data.labels,
                    data: res.data.data.map((num) => parseFloat(num.toFixed(2))),
                };
                setRevenueBarChartData(formattedData);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // pie  chart data
    const getLabelTypePieChart = async () => {
        await api.get('/dashboard/read-labels-chart').then((res) => {
            setLabelPieChartData(res.data);
        });
    };

    const getPaymentpieChart = async () => {
        await api.get('/dashboard/read-payment-chart').then((res) => {
            setPaymentPieChartData(res.data);
        });
    };

    // profit
    const getAllNetProfitStat = async () => {
        await api
            .get('/dashboard/readnetprofit')
            .then((res) => {
                setNetProfitData(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // call all the functions at once
    const callAll = async () => {
        // promise all
        await Promise.all([
            getOrdersStat(),
            getRevenueStats(),
            getUsersStats(),
            getDepositStats(),
            readLabelTypeStats(),
            getOrdersBarChart(),
            getUsersBarChart(),
            getDepositsBarChart(),
            getRevenueBarChart(),
            getLabelTypePieChart(),
            getPaymentpieChart(),
            getAllNetProfitStat(),
        ]);
    };

    useEffect(() => {
        callAll();
    }, []);

    return (
        <PageContainer title={'Hello ' + user.username + '!'} desc="All your statistics at one place.">
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={3}>
                    <StatCard icon={<Icon path="bulk/receipt-2" />} name="Orders" values={Object.values(OrderStats)} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <StatCard
                        icon={<Icon path="bulk/wallet" />}
                        name="Revenue"
                        values={Object.values(revenueStats)}
                        currency
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <StatCard icon={<Icon path="bulk/coin" />} name="Users" values={Object.values(usersStats)} />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <StatCard
                        icon={<Icon path="bulk/ticket" />}
                        name="Deposits"
                        values={Object.values(depositStats)}
                        currency
                    />
                </Grid>
            </Grid>
            <Box mb={2}>
                <StatCard
                    icon={<Icon path="bulk/receipt-2" />}
                    name="Net Profit"
                    values={Object.values(netProfitData)}
                    currency
                    horizontal
                />
            </Box>

            <CustomTable title="Type Statistics" fields={['TYPE', 'TODAY', 'WEEK', 'MONTH', 'ALLTIME']}>
                {labelTypesData?.map((label) => (
                    <TableRow>
                        <TableCell>{label?.labeltype?.name}</TableCell>
                        <TableCell>{label?.todayOrdersCount}</TableCell>
                        <TableCell>{label?.thisWeekOrdersCount}</TableCell>
                        <TableCell>{label?.thisMonthOrdersCount}</TableCell>
                        <TableCell>{label?.allTimeOrdersCount}</TableCell>
                    </TableRow>
                ))}
            </CustomTable>
            <Grid container spacing={3} mt={1}>
                <Grid item xs={12} sm={6}>
                    <Section title="Orders Graph">
                        <BarChart data={ordersBarChartData} name={'Orders'} />
                    </Section>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Section title="Users Graph">
                        <BarChart data={usersBarChartData} name={'Users '} />
                    </Section>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Section title="Total Deposits">
                        <BarChart data={depositBarChartData} name={'Total Deposits'} />
                    </Section>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Section title="Revenue">
                        <BarChart data={revenueBarChartData} name={'Revenue'} />
                    </Section>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Section title="Label Type Distribution">
                        <DonutChart data={labelPieChartData} />
                    </Section>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Section title="Payment Gateway Distribution">
                        <DonutChartPayment data={PaymentPieChartData} />
                    </Section>
                </Grid>
            </Grid>
        </PageContainer>
    );
};

export default Dashboard;
